import type { ReactNode } from "react";
import { IconDownload, IconSearch } from "@tabler/icons-react";
import {
  AGING_BUCKET_FILTER_OPTIONS,
  INVOICE_TYPE_FILTER_OPTIONS,
  toggleRiskLevelFilter,
} from "./helpers";
import type { InvoiceTypeFilter, RiskLevelFilters, ViewBy } from "./types";

export function ReceivablesSharedControls({
  search,
  onSearchChange,
  viewBy,
  onViewByChange,
  riskLevels,
  onRiskLevelsChange,
  invoiceTypeFilter,
  onInvoiceTypeFilterChange,
  showInvoiceTypeFilter = false,
  onDownload,
  bulkActions,
}: {
  search: string;
  onSearchChange: (value: string) => void;
  viewBy: ViewBy;
  onViewByChange: (value: ViewBy) => void;
  riskLevels: RiskLevelFilters;
  onRiskLevelsChange: (value: RiskLevelFilters) => void;
  invoiceTypeFilter: InvoiceTypeFilter;
  onInvoiceTypeFilterChange: (value: InvoiceTypeFilter) => void;
  showInvoiceTypeFilter?: boolean;
  onDownload: () => void;
  bulkActions?: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-3 border-b border-slate-200 px-4 py-3 lg:flex-row lg:items-center">
      <div className="flex items-center gap-2 text-sm text-slate-600">
        <span className="font-medium">View By:</span>
        <div className="inline-flex rounded-md bg-slate-100 p-0.5">
          <button
            type="button"
            onClick={() => onViewByChange("customers")}
            className={`cursor-pointer rounded px-3 py-1.5 text-sm ${
              viewBy === "customers"
                ? "bg-white font-semibold text-slate-900 shadow-sm"
                : "text-slate-500"
            }`}
          >
            Customers
          </button>
          <button
            type="button"
            onClick={() => onViewByChange("invoices")}
            className={`cursor-pointer rounded px-3 py-1.5 text-sm ${
              viewBy === "invoices"
                ? "bg-white font-semibold text-slate-900 shadow-sm"
                : "text-slate-500"
            }`}
          >
            Invoices
          </button>
          <button
            type="button"
            onClick={() => onViewByChange("emails")}
            className={`cursor-pointer rounded px-3 py-1.5 text-sm ${
              viewBy === "emails"
                ? "bg-white font-semibold text-slate-900 shadow-sm"
                : "text-slate-500"
            }`}
          >
            Emails
          </button>
        </div>
      </div>

      <div className="relative min-w-0 flex-1">
        <IconSearch
          size={16}
          className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
        />
        <input
          value={search}
          onChange={(event) => onSearchChange(event.target.value)}
          placeholder={
            viewBy === "emails"
              ? "Search mail"
              : viewBy === "customers"
                ? "Search customer name or client ID"
                : "Search customer or invoice"
          }
          className="w-1/2 rounded-md border border-slate-200 py-2 pl-9 pr-3 text-sm text-slate-700"
        />
      </div>

      {viewBy !== "emails" ? (
        <div className="flex items-center gap-2 text-sm text-slate-600">
          <span className="font-medium">Aging:</span>
          <div className="inline-flex max-w-full flex-wrap rounded-md bg-slate-100 p-0.5">
            <button
              type="button"
              onClick={() => onRiskLevelsChange(toggleRiskLevelFilter(riskLevels, "all"))}
              className={`inline-flex cursor-pointer items-center gap-1.5 rounded px-2.5 py-1.5 text-sm ${
                riskLevels.length === 0
                  ? "bg-white font-semibold text-slate-900 shadow-sm"
                  : "text-slate-500"
              }`}
            >
              All
            </button>
            {AGING_BUCKET_FILTER_OPTIONS.map((option) => {
              const isSelected = riskLevels.includes(option.value);
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() =>
                    onRiskLevelsChange(toggleRiskLevelFilter(riskLevels, option.value))
                  }
                  className={`inline-flex cursor-pointer items-center gap-1.5 rounded px-2.5 py-1.5 text-sm ${
                    isSelected
                      ? `bg-white font-semibold shadow-sm ${option.textClass ?? "text-slate-900"}`
                      : "text-slate-500"
                  }`}
                >
                  {option.dotClass ? (
                    <span
                      className={`h-2 w-2 shrink-0 rounded-full ${option.dotClass}`}
                    />
                  ) : null}
                  {option.label}
                </button>
              );
            })}
          </div>
        </div>
      ) : null}

      {viewBy !== "emails" && showInvoiceTypeFilter ? (
        <div className="flex items-center gap-2 text-sm text-slate-600">
          <span className="font-medium">Type:</span>
          <div className="inline-flex rounded-md bg-slate-100 p-0.5">
            {INVOICE_TYPE_FILTER_OPTIONS.map((option) => {
              const isSelected = invoiceTypeFilter === option.value;
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => onInvoiceTypeFilterChange(option.value)}
                  className={`cursor-pointer rounded px-2.5 py-1.5 text-sm ${
                    isSelected
                      ? "bg-white font-semibold text-slate-900 shadow-sm"
                      : "text-slate-500"
                  }`}
                >
                  {option.label}
                </button>
              );
            })}
          </div>
        </div>
      ) : null}

      {viewBy !== "emails" ? (
        <div className="flex items-center gap-2">
          {(viewBy === "invoices" || viewBy === "customers") && bulkActions
            ? bulkActions
            : null}
          <button
            type="button"
            onClick={onDownload}
            className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-slate-200 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            <IconDownload size={16} />
            Download
          </button>
        </div>
      ) : null}
    </div>
  );
}